import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import {
    Row, Col, Table, Button, Form, Modal, InputGroup,
    OverlayTrigger,
    Tooltip
} from 'react-bootstrap';
import {
    PlusLg, PencilSquare, Trash3, Search,
    ChevronLeft, ChevronRight, ArrowLeft,
    Clock, Funnel, Tag, CurrencyRupee
} from 'react-bootstrap-icons';
import { AlertCircle, Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRICE_CHANGE_TYPES = [
    { value: 'INCREASE', label: 'Increase' },
    { value: 'DECREASE', label: 'Decrease' },
];

const VALUE_TYPES = [
    { value: 'PERCENTAGE', label: 'Percentage (%)' },
    { value: 'AMOUNT', label: 'Fixed Amount (₹)' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractName = (val) => {
    if (!val) return '—';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.name || val.title || val._id || '—';
    return '—';
};

const safe = (val, fallback = '—') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return val.name || val._id || fallback;
    return String(val);
};

const toMultiOptions = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map(item => {
        if (!item) return null;
        if (typeof item === 'string') return { value: item, label: item };
        return { value: item._id || item, label: item.name || item.title || item._id || item };
    }).filter(Boolean);
};

const formatDateLocal = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const selectStyles = (isDark) => ({
    control: (base, state) => ({
        ...base,
        fontSize: '13px',
        minHeight: '38px',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#4f46e5' : (isDark ? '#2c3242' : '#e1e4ea'),
        backgroundColor: isDark ? '#11151f' : '#fff',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
        '&:hover': { borderColor: '#4f46e5' },
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: isDark ? '#1b2030' : '#fff',
        border: `1px solid ${isDark ? '#2c3242' : '#e1e4ea'}`,
        zIndex: 9999,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected
            ? '#4f46e5'
            : state.isFocused
                ? (isDark ? '#1f2350' : '#eef0ff')
                : 'transparent',
        color: state.isSelected ? '#fff' : (isDark ? '#e7e9ee' : '#1f2430'),
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: isDark ? '#1f2350' : '#eef0ff',
        borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
        ...base,
        fontSize: '12px',
        color: isDark ? '#9aa3ff' : '#4338ca',
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: isDark ? '#9aa3ff' : '#4338ca',
        ':hover': { backgroundColor: isDark ? '#2d3360' : '#ddd6fe', color: '#4338ca' },
    }),
    placeholder: (base) => ({ ...base, fontSize: '13px', color: isDark ? '#5a6070' : '#9aa1ac' }),
    singleValue: (base) => ({ ...base, fontSize: '13px', color: isDark ? '#e7e9ee' : '#1f2430' }),
    input: (base) => ({ ...base, color: isDark ? '#e7e9ee' : '#1f2430' }),
});

// ─── Default form ─────────────────────────────────────────────────────────────

const defaultForm = {
    eventDate: null,
    eventTime: '',
    applicableMenuGroup: null,
    priceChangeType: 'INCREASE',
    valueType: 'PERCENTAGE',
    changeValue: '',
    applicableCategory: [],
    applicableProduct: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

const PricingUpdateEvents = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('accessToken');
    const navigate = useNavigate();
    const isDark = skin === 'dark';

    // list
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // dropdown options
    const [menuGroupOptions, setMenuGroupOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    // form / modal
    const [showModal, setShowModal] = useState(false);
    // const [isEditing, setIsEditing] = useState(false);  // EDIT: commented out
    // const [editId, setEditId]       = useState(null);   // EDIT: commented out
    const [form, setForm] = useState(defaultForm);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    // delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const [selectedMenuGroup, setSelectedMenuGroup] = useState(null);

    // filters / pagination
    const [search, setSearch] = useState('');
    const [filterChange, setFilterChange] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [page, setPage] = useState(1);

    // ─── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => { fetchEvents(page); }, [page]);
    useEffect(() => {
        fetchMenuGroups();
        fetchCategoryOptions();
        fetchProductOptions();
    }, []);

    // ─── Fetch list ───────────────────────────────────────────────────────────
    const fetchEvents = async (pageNum = 1) => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('pricing-update', { params: { page: pageNum, limit: 10 } });
            const payload = res.data.data;
            setEvents(payload.dbResponse || []);
            setPagination(payload.pagination || { totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    // ─── Fetch dropdown options ───────────────────────────────────────────────
    const fetchMenuGroups = async () => {
        try {
            const res = await axiosInstance.get('menu-group', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            const list = res?.data?.data?.menuGroup || res?.data?.data || [];
            setMenuGroupOptions(Array.isArray(list) ? list.map(m => ({ value: m._id, label: m.groupName })) : []);
        } catch (e) {
            console.error('Menu group fetch error', e);
        }
    };

    const fetchCategoryOptions = async () => {
        try {
            const res = await axiosInstance.get('masters/category/get', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.categories || [];
            setCategoryOptions(list.map(c => ({ value: c._id, label: c.name })));
        } catch (e) { console.error('Category fetch error', e); }
    };

    const fetchProductOptions = async () => {
        try {
            const res = await axiosInstance.get('product', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.products || [];
            setProductOptions(list.map(p => ({ value: p._id, label: p.name })));
        } catch (e) { console.error('Product fetch error', e); }
    };

    // ─── Modal helpers ────────────────────────────────────────────────────────
    const openAddModal = () => {
        // setIsEditing(false);  // EDIT: commented out
        // setEditId(null);      // EDIT: commented out
        setForm(defaultForm);
        setFormError('');
        setShowModal(true);
    };

    // ─── EDIT: openEditModal commented out ────────────────────────────────────
    // const openEditModal = (ev) => {
    //     setIsEditing(true);
    //     setEditId(ev._id);
    //
    //     const toSingle = (val) => {
    //         if (!val) return null;
    //         if (typeof val === 'object') return { value: val._id, label: val.name || val.title || val._id };
    //         return { value: val, label: val };
    //     };
    //
    //     setForm({
    //         eventDate:           ev.eventDate ? new Date(ev.eventDate) : null,
    //         eventTime:           ev.eventTime || '',
    //         applicableMenuGroup: toSingle(ev.applicableMenuGroup),
    //         priceChangeType:     ev.priceChangeType || 'INCREASE',
    //         valueType:           ev.valueType || 'PERCENTAGE',
    //         changeValue:         ev.changeValue || '',
    //         applicableCategory:  toMultiOptions(ev.applicableCategory),
    //         applicableProduct:   toMultiOptions(ev.applicableProduct),
    //     });
    //     setFormError('');
    //     setShowModal(true);
    // };

    // ─── Form handlers ────────────────────────────────────────────────────────
   const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'changeValue' && form.valueType === 'PERCENTAGE') {
        const numValue = Number(value);

        if (numValue > 100) {
            return; // Prevent values greater than 100
        }
    }

    if (name === 'valueType') {
        setForm(prev => ({
            ...prev,
            valueType: value,
            changeValue: '', // Reset when switching between % and ₹
        }));
        return;
    }

    setForm(prev => ({
        ...prev,
        [name]: value,
    }));
};
    // const handleFormChange = (e) => {
    //     const { name, value } = e.target;
    //     setForm(prev => ({ ...prev, [name]: value }));
    // };

    const handleCategoryChange = (options) => {
        setForm(prev => ({ ...prev, applicableCategory: options || [], applicableProduct: [] }));
    };

    const handleProductChange = (options) => {
        setForm(prev => ({ ...prev, applicableProduct: options || [], applicableCategory: [] }));
    };

    // ─── Build payload ────────────────────────────────────────────────────────
    const buildPayload = () => {

        if (!form.applicableMenuGroup?.value) {
            setFormError('Please select a menu group'); 
            toast.error('Please Select a Menu Group', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
            return null;
        }
        if (!form.changeValue || isNaN(Number(form.changeValue)) || Number(form.changeValue) <= 0) {
            setFormError('Please enter a valid change value greater than 0'); 
            toast.error('Please Enter Price Change Value Greater Than 0', {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
            return null;
        }
        // if (form.applicableCategory.length === 0 && form.applicableProduct.length === 0) {
        //     setFormError('Please select at least one category or product'); return null;
        // }

        const payload = {
            // eventDate:           form.eventDate ? form.eventDate.toISOString().split('T')[0] : '',
            eventDate: form.eventDate
                ? formatDateLocal(form.eventDate)
                : '',
            applicableMenuGroup: form.applicableMenuGroup.value,
            priceChangeType: form.priceChangeType,
            valueType: form.valueType,
            changeValue: String(form.changeValue),
        };

        if (form.eventTime) payload.eventTime = form.eventTime;
        if (form.applicableCategory.length > 0) {
            payload.applicableCategory = form.applicableCategory.map(o => o.value);
        }
        if (form.applicableProduct.length > 0) {
            payload.applicableProduct = form.applicableProduct.map(o => o.value);
        }

        // ─── EDIT: edit-specific payload field commented out ──────────────────
        // if (isEditing) payload._id = editId;

        return payload;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setFormError('');
        if (!form.eventDate) { setFormError('Event date is required'); return; }
        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        try {
            // ─── EDIT: conditional put/post simplified to post only ────────────
            // if (isEditing) {
            //     await axiosInstance.put('pricing-update', payload);
            // } else {
            //     await axiosInstance.post('pricing-update', payload);
            // }
            await axiosInstance.post('pricing-update', payload);

            toast.success('Pricing event created successfully', {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
            setShowModal(false);
            fetchEvents(page);
        } catch (error) {
            // setFormError(err?.response?.data?.message || err.message || 'Save failed');
            let errorMessage = '';
            if (error.response?.status === 406) {
                errorMessage = error?.response?.data?.error || error?.response?.data?.error?.message
            }
            else if (error.response?.status === 412) {
                errorMessage = error?.response?.data?.data?.message;
            } else if (error.response?.status === 500) {
                errorMessage = error?.response?.data?.error
            } else if (error?.response?.status === 404) {
                errorMessage = error?.response?.data?.error
            }

            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setSaving(false);
        }
    };

    // ─── Delete ───────────────────────────────────────────────────────────────
    const openDeleteModal = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
    };

    const confirmDelete = async () => {
        const id = deleteTargetId;
        if (!id) return;
        setDeleting(id);
        try {
            await axiosInstance.delete('pricing-update', { data: { _id: id } });
            toast.success('Pricing event deleted', { position: 'top-right', autoClose: 3000, theme: 'light' });
            setShowDeleteModal(false);
            setDeleteTargetId(null);
            if (events.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            } else {
                fetchEvents(page);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Delete failed', {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
        } finally {
            setDeleting(null);
        }
    };

    // ─── Filter ───────────────────────────────────────────────────────────────
    const filteredEvents = events?.filter(ev => {
        const groupName = extractName(ev.applicableMenuGroup);
        const matchMenugroup = !selectedMenuGroup?.value || selectedMenuGroup.value === ev?.applicableMenuGroup?._id;
        const matchSearch = !search || groupName.toLowerCase().includes(search.toLowerCase());
        const matchChange = !filterChange || ev.priceChangeType === filterChange;
        const matchStatus = !filterStatus || ev.status === filterStatus;
        return matchSearch && matchChange && matchStatus && matchMenugroup;
    });

    const totalIncrease = events?.filter(e => e?.priceChangeType === 'INCREASE')?.length;
    const totalDecrease = events?.filter(e => e?.priceChangeType === 'DECREASE')?.length;

    const sl = selectStyles(isDark);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ae-page ${isDark ? 'ae-dark bg-dark text-light' : 'bg-light'}`}>

                {/* Global styles for modal portal (rendered outside .ae-page) */}
                <style id="pu-modal-global">{`
                    .pu-modal .modal-content {
                        border: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        border-radius: 12px;
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                        overflow: hidden;
                        box-shadow: 0 20px 60px rgba(0,0,0,0.18);
                    }
                    .pu-modal .modal-header {
                        border-bottom: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#1b2030' : '#f8f9fb'};
                        padding: 16px 20px;
                    }
                    .pu-modal .modal-title {
                        font-size: 15px;
                        font-weight: 700;
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                    }
                    .pu-modal .modal-header .btn-close {
                        filter: ${isDark ? 'invert(1) grayscale(1)' : 'none'};
                    }
                    .pu-modal .modal-body {
                        padding: 20px;
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                        max-height: 75vh;
                        overflow-y: auto;
                    }
                    .pu-modal .modal-footer {
                        border-top: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        padding: 14px 20px;
                        gap: 8px;
                    }
                    .pu-modal-subtitle {
                        font-size: 12.5px;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        margin-top: 4px;
                        margin-bottom: 0;
                    }
                    .pu-modal .form-control,
                    .pu-modal .form-select {
                        font-size: 13px;
                        border-radius: 6px;
                        border-color: ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#11151f' : '#ffffff'};
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                    }
                    .pu-modal .form-control:focus,
                    .pu-modal .form-select:focus {
                        border-color: #4f46e5;
                        box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
                    }
                    .pu-modal .input-group-text {
                        font-size: 13px;
                        border-color: ${isDark ? '#2c3242' : '#e6e8ec'};
                        background: ${isDark ? '#1b2030' : '#f8f9fb'};
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                    }
                    .pu-modal .react-datepicker-wrapper { width: 100%; }
                    .pu-modal-section-title {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.09em;
                        font-weight: 700;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        padding-bottom: 8px;
                        margin-bottom: 14px;
                        border-bottom: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                    }
                    .pu-modal-field-label {
                        font-size: 12.5px;
                        font-weight: 600;
                        color: ${isDark ? '#e7e9ee' : '#1f2430'};
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        margin-bottom: 6px;
                    }
                    .pu-modal-field-hint {
                        font-size: 11px;
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        margin-top: 5px;
                        margin-bottom: 0;
                    }
                    .pu-modal-error-box {
                        display: flex; gap: 8px; align-items: flex-start;
                        background: ${isDark ? '#2a1414' : '#fef2f2'};
                        border: 1px solid ${isDark ? '#4a1f1f' : '#fecaca'};
                        color: ${isDark ? '#f3a3a3' : '#b91c1c'};
                        border-radius: 6px; padding: 10px 12px; font-size: 12.5px; margin-bottom: 16px;
                    }
                    .pu-modal-toggle-group {
                        display: flex;
                        border: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                        border-radius: 8px;
                        overflow: hidden;
                    }
                    .pu-modal-toggle-btn {
                        flex: 1;
                        padding: 8px 12px;
                        font-size: 12.5px;
                        font-weight: 600;
                        border: none;
                        background: ${isDark ? '#1b2030' : '#f8f9fb'};
                        color: ${isDark ? '#8a93a6' : '#6b7280'};
                        cursor: pointer;
                        transition: all .12s;
                        display: flex; align-items: center; justify-content: center; gap: 6px;
                    }
                    .pu-modal-toggle-btn:not(:last-child) {
                        border-right: 1px solid ${isDark ? '#2c3242' : '#e6e8ec'};
                    }
                    .pu-modal-toggle-btn.active-increase { background: #c3ffe3; color: #047857; }
                    .pu-modal-toggle-btn.active-decrease { background: #ffbebe; color: #b91c1c; }
                    .pu-modal-mutual-note {
                        font-size: 11px;
                        background: ${isDark ? '#1f2350' : '#eef0ff'};
                        color: ${isDark ? '#9aa3ff' : '#4338ca'};
                        padding: 6px 10px;
                        border-radius: 6px;
                        margin-bottom: 12px;
                    }
                    .pu-modal-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    }
                    .pu-modal-grid .pu-full-col { grid-column: 1 / -1; }
                    .pu-btn-primary {
                        background: #4f46e5 !important;
                        border-color: #4f46e5 !important;
                        font-size: 13px;
                        font-weight: 600;
                        color: #fff !important;
                    }
                    .pu-btn-primary:hover { background: #4338ca !important; border-color: #4338ca !important; }
                    .pu-btn-primary:disabled { opacity: 0.7; }
                    @keyframes pu-spin { to { transform: rotate(360deg); } }
                `}</style>

                {/* Scoped page styles */}
                <style>{`
                    .ae-page {
                        --ae-border: ${isDark ? '#2c3242' : '#e6e8ec'};
                        --ae-bg-subtle: ${isDark ? '#1b2030' : '#f8f9fb'};
                        --ae-surface: ${isDark ? '#11151f' : '#ffffff'};
                        --ae-text: ${isDark ? '#e7e9ee' : '#1f2430'};
                        --ae-muted: ${isDark ? '#8a93a6' : '#6b7280'};
                        --ae-accent: #4f46e5;
                        --ae-mono: 'SFMono-Regular', Menlo, Consolas, monospace;
                        font-feature-settings: "tnum" 1;
                    }
                    .ae-crumb { font-size: 12px; color: var(--ae-muted); }
                    .ae-crumb b { color: var(--ae-text); font-weight: 600; }
                    .ae-page-title { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; color: var(--ae-text); }
                    .ae-page-sub { font-size: 12.5px; color: var(--ae-muted); }

                    .ae-stat-bar { display: flex; border: 1px solid var(--ae-border); border-radius: 10px; background: var(--ae-surface); overflow: hidden; }
                    .ae-stat { flex: 1; min-width: 0; padding: 12px 18px; display: flex; flex-direction: column; gap: 4px; border-right: 1px solid var(--ae-border); }
                    .ae-stat:last-child { border-right: none; }
                    .ae-stat-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; color: var(--ae-muted); display: flex; align-items: center; gap: 6px; }
                    .ae-stat-value { font-family: var(--ae-mono); font-size: 22px; font-weight: 700; color: var(--ae-text); line-height: 1.1; }

                    .ae-card { border: 1px solid var(--ae-border); border-radius: 10px; background: var(--ae-surface); overflow: hidden; }
                    .ae-toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; padding: 10px 14px; border-bottom: 1px solid var(--ae-border); background: var(--ae-bg-subtle); position: relative; z-index: 10; }
                    .ae-toolbar .form-control, .ae-toolbar .form-select, .ae-toolbar .input-group-text { font-size: 12.5px; height: 34px; border-color: var(--ae-border); background: var(--ae-surface); color: var(--ae-text); }
                    .ae-toolbar .form-select { padding-top: 0; padding-bottom: 0; }

                    .ae-table { margin-bottom: 0; font-size: 12.5px; }
                    .ae-table thead th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.07em; font-weight: 700; color: var(--ae-muted); background: var(--ae-bg-subtle); border-bottom: 1px solid var(--ae-border); padding: 9px 14px; white-space: nowrap; }
                    .ae-table tbody td { padding: 9px 14px; vertical-align: middle; border-bottom: 1px solid var(--ae-border); color: var(--ae-text); }
                    .ae-table tbody tr:last-child td { border-bottom: none; }
                    .ae-table tbody tr:hover { background: var(--ae-bg-subtle); }
                    .ae-mono { font-family: var(--ae-mono); font-size: 12px; color: var(--ae-muted); }
                    .ae-row-idx { font-family: var(--ae-mono); font-size: 11.5px; color: var(--ae-muted); }
                    .ae-ellipsis { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; }

                    .ae-badge { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 4px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap; }
                    .ae-badge-increase   { background: ${isDark ? '#10301f' : '#ecfdf5'}; color: ${isDark ? '#5fd9a4' : '#047857'}; }
                    .ae-badge-decrease   { background: ${isDark ? '#3a1414' : '#fef2f2'}; color: ${isDark ? '#f3a3a3' : '#b91c1c'}; }
                    .ae-badge-percentage { background: ${isDark ? '#1f2350' : '#eef0ff'}; color: ${isDark ? '#9aa3ff' : '#4338ca'}; }
                    .ae-badge-amount     { background: ${isDark ? '#3a2c10' : '#fffbeb'}; color: ${isDark ? '#f0c468' : '#b45309'}; }
                    .ae-badge-pending    { background: ${isDark ? '#3a2c10' : '#fffbeb'}; color: ${isDark ? '#f0c468' : '#b45309'}; }
                    .ae-badge-completed  { background: ${isDark ? '#10301f' : '#ecfdf5'}; color: ${isDark ? '#5fd9a4' : '#047857'}; }
                    .ae-badge-failed     { background: ${isDark ? '#3a1414' : '#fef2f2'}; color: ${isDark ? '#f3a3a3' : '#b91c1c'}; }

                    .ae-tag-list { display: flex; flex-wrap: wrap; gap: 4px; }
                    .ae-tag { font-size: 11px; padding: 1px 7px; border-radius: 4px; background: var(--ae-bg-subtle); border: 1px solid var(--ae-border); color: var(--ae-text); white-space: nowrap; }

                    .ae-icon-btn { width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; border: 1px solid var(--ae-border); background: var(--ae-surface); color: var(--ae-muted); transition: all .12s ease; }
                    .ae-icon-btn:hover { color: var(--ae-text); border-color: var(--ae-accent); background: ${isDark ? '#1b1f33' : '#eef0ff'}; }
                    .ae-icon-btn-danger:hover { color: #dc2626; border-color: #fecaca; background: ${isDark ? '#2a1414' : '#fef2f2'}; }
                    .ae-icon-btn:disabled { opacity: .5; cursor: not-allowed; }

                    .ae-pagination { display: flex; align-items: center; gap: 6px; }
                    .ae-page-btn { min-width: 28px; height: 28px; padding: 0 6px; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; font-size: 12px; font-weight: 600; border: 1px solid var(--ae-border); background: var(--ae-surface); color: var(--ae-text); transition: all .12s ease; }
                    .ae-page-btn:hover:not(:disabled) { border-color: var(--ae-accent); }
                    .ae-page-btn:disabled { opacity: .4; }
                    .ae-page-btn.active { background: var(--ae-accent); border-color: var(--ae-accent); color: #fff; }

                    .ae-btn-primary { background: var(--ae-accent); border-color: var(--ae-accent); font-size: 13px; font-weight: 600; }
                    .ae-btn-primary:hover { background: #4338ca; border-color: #4338ca; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>

                {/* Page Header */}
                <div className="ae-card mb-3" style={{ padding: '14px 18px' }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex justify-content-center align-items-center gap-1 px-2 py-1 w-25"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => navigate('/events')}
                                >
                                    <ArrowLeft size={13} /> Back
                                </Button>
                                {/* <span className="ae-crumb">All events / <b>Pricing update events</b></span> */}
                            </div>
                            <h4 className="ae-page-title mb-1">Pricing update events</h4>
                            <p className="ae-page-sub mb-0">
                                Schedule price increases or decreases for menu groups by percentage or fixed amount.
                            </p>
                        </div>
                        <Button className="ae-btn-primary d-flex align-items-center gap-2 w-25 justify-content-center" onClick={openAddModal}>
                            <PlusLg size={15} /> Add event
                        </Button>
                    </div>
                </div>

                {/* Stat strip */}
                {/* <div className="ae-stat-bar mb-3">
                    <div className="ae-stat">
                        <span className="ae-stat-label">Total records</span>
                        <span className="ae-stat-value">{pagination.totalRecords}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label" style={{ color: '#047857' }}>↑ Increase</span>
                        <span className="ae-stat-value" style={{ color: isDark ? '#5fd9a4' : '#047857' }}>{totalIncrease}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label" style={{ color: '#b91c1c' }}>↓ Decrease</span>
                        <span className="ae-stat-value" style={{ color: isDark ? '#f3a3a3' : '#b91c1c' }}>{totalDecrease}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label">Page</span>
                        <span className="ae-stat-value">
                            {pagination.currentPage}
                            <span style={{ fontSize: '13px', color: 'var(--ae-muted)', fontWeight: 600 }}> / {pagination.totalPages}</span>
                        </span>
                    </div>
                </div> */}

                {/* Table Card */}
                <div className="ae-card">

                    {/* Toolbar */}
                    <div className="ae-toolbar">
                        <div style={{ minWidth: '220px' }}>
                            <Select
                                options={menuGroupOptions}
                                value={selectedMenuGroup}
                                onChange={(option) => setSelectedMenuGroup(option)}
                                placeholder="Select menu group…"
                                isClearable
                                isSearchable
                                styles={sl}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        <InputGroup style={{ width: 'auto' }}>
                            <InputGroup.Text className="border-end-0"><Funnel size={12} /></InputGroup.Text>
                            <Form.Select
                                style={{ width: '160px', borderLeft: 'none' }}
                                value={filterChange}
                                onChange={e => setFilterChange(e.target.value)}
                            >
                                <option value="">Price Changes</option>
                                <option value="INCREASE">Increase</option>
                                <option value="DECREASE">Decrease</option>
                            </Form.Select>
                        </InputGroup>
                        <Form.Select
                            style={{ width: '140px' }}
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                        >
                            <option value="">Event Status</option>
                            <option value="PENDING">Pending</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="FAILED">Failed</option>
                        </Form.Select>
                        {(search || filterChange || filterStatus || selectedMenuGroup) && (
                            <Button
                                variant="link" className="p-0 ms-auto"
                                style={{ fontSize: '12px', textDecoration: 'none' }}
                                onClick={() => { setSearch(''); setFilterChange(''); setFilterStatus(''); setSelectedMenuGroup(null); }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="text-center py-5">
                            <Loader2 size={20} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--ae-accent)' }} />
                            <p className="ae-page-sub mt-2 mb-0">Loading events…</p>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="d-flex flex-column align-items-center py-5 gap-2" style={{ color: '#dc2626' }}>
                            <AlertCircle size={26} />
                            <p className="mb-1" style={{ fontSize: '13px' }}>{error}</p>
                            <Button size="sm" variant="outline-danger" onClick={() => fetchEvents(page)}>Retry</Button>
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && filteredEvents.length === 0 && (
                        <div className="text-center py-5">
                            <CurrencyRupee size={32} style={{ color: 'var(--ae-muted)' }} className="mb-2" />
                            <p className="ae-page-sub mb-0">No pricing events match these filters</p>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && filteredEvents.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <Table className="ae-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '44px' }}>Sr No</th>
                                        <th>Menu group</th>
                                        <th>Price Change</th>
                                        <th>Price Change Based On</th>
                                        <th>Price Change Value</th>
                                        <th>Applies to</th>
                                        <th>Date & Time</th>
                                        {/* <th>Time</th> */}
                                        <th>Status</th>
                                        <th style={{ width: '60px' }} className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEvents.map((ev, idx) => {
                                        const cats = Array.isArray(ev.applicableCategory) ? ev.applicableCategory : [];
                                        const prods = Array.isArray(ev.applicableProduct) ? ev.applicableProduct : [];
                                        const appliesToItems = [...cats, ...prods];

                                        return (
                                            <tr key={ev._id}>
                                                <td className="ae-row-idx">
                                                    {String((pagination.currentPage - 1) * pagination.limit + idx + 1).padStart(2, '0')}
                                                </td>
                                                <td>
                                                    <span className="ae-ellipsis" title={extractName(ev.applicableMenuGroup)}>
                                                        {extractName(ev?.applicableMenuGroup?.groupName)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`ae-badge ae-badge-${ev.priceChangeType?.toLowerCase() || 'pending'}`}>
                                                        {ev.priceChangeType === 'INCREASE'
                                                            ? <TrendingUp size={10} />
                                                            : <TrendingDown size={10} />}
                                                        {safe(ev.priceChangeType)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`ae-badge ae-badge-${ev.valueType === 'PERCENTAGE' ? 'percentage' : 'amount'}`}>
                                                        {ev.valueType === 'PERCENTAGE' ? '%' : '₹'} {safe(ev.valueType)}
                                                    </span>
                                                </td>
                                                <td className="ae-mono text-end" style={{ fontWeight: 700, color: 'var(--ae-text)' }}>
                                                    {ev.changeValue
                                                        ? `${ev.valueType === 'PERCENTAGE' ? '' : '₹'}${ev.changeValue}${ev.valueType === 'PERCENTAGE' ? '%' : ''}`
                                                        : '—'}
                                                </td>
                                                <td>
                                                    {appliesToItems.length > 0 ? (
                                                        <div className="ae-tag-list">
                                                            {appliesToItems.slice(0, 2).map((item, i) => (
                                                                <span key={i} className="ae-tag">{extractName(item)}</span>
                                                            ))}
                                                            {/* {appliesToItems.length > 2 && (
                                                                <span className="ae-tag" style={{ color: 'var(--ae-muted)' }}>
                                                                    +{appliesToItems.length - 2} more
                                                                </span>
                                                            )} */}
                                                            {appliesToItems.length > 2 && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`items-tooltip-${ev._id}`}>
                                                                            <div className="text-start">
                                                                                {appliesToItems.slice(2).map((item, index) => (
                                                                                    <div key={index}>
                                                                                        {extractName(item)}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span
                                                                        className="ae-tag"
                                                                        style={{
                                                                            color: 'var(--ae-muted)',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        +{appliesToItems.length - 2} more
                                                                    </span>
                                                                </OverlayTrigger>
                                                            )}
                                                        </div>
                                                    ) : 'Overall Menu'}
                                                </td>
                                                <td className="ae-mono">
                                                    {ev.eventDate
                                                        ? new Date(ev.eventDate).toLocaleDateString('en-IN', {
                                                            day: '2-digit', month: 'short', year: 'numeric',
                                                        })
                                                        : '—'}
                                                    <br />
                                                    {safe(ev.eventTime)}
                                                </td>
                                                {/* <td className="ae-mono">{safe(ev.eventTime)}</td> */}
                                                <td>
                                                    <span className={`ae-badge ae-badge-${(ev.status || 'pending').toLowerCase()}`}>
                                                        {safe(ev.status, 'PENDING')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        {/* EDIT: Edit button commented out */}
                                                        {/* <button
                                                            className="ae-icon-btn"
                                                            onClick={() => openEditModal(ev)}
                                                            title="Edit"
                                                        >
                                                            <PencilSquare size={13} />
                                                        </button> */}
                                                        <button
                                                            className="ae-icon-btn ae-icon-btn-danger"
                                                            disabled={deleting === ev._id}
                                                            onClick={() => openDeleteModal(ev._id)}
                                                            title="Delete"
                                                        >
                                                            {deleting === ev._id
                                                                ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
                                                                : <Trash3 size={13} />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && pagination.totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between px-3 py-2"
                            style={{ borderTop: '1px solid var(--ae-border)' }}>
                            <p className="ae-page-sub mb-0">
                                Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalRecords} total records
                            </p>
                            <div className="ae-pagination">
                                <button className="ae-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                    <ChevronLeft size={13} />
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`ae-page-btn ${p === page ? 'active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button className="ae-page-btn" disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)}>
                                    <ChevronRight size={13} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Add Modal ────────────────────────────────────────────── */}
                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    centered
                    size="lg"
                    className="pu-modal"
                >
                    <Modal.Header closeButton>
                        <div>
                            <Modal.Title>Add Pricing Event</Modal.Title>
                            <p className="pu-modal-subtitle">
                                Schedule an automatic price increase or decrease for a menu group.
                            </p>
                        </div>
                    </Modal.Header>

                    <Modal.Body>
                        {formError && (
                            <div className="pu-modal-error-box">
                                <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />
                                <span>{formError}</span>
                            </div>
                        )}

                        {/* ── Menu group — full width ───────────────────────── */}
                        <div className="pu-modal-section-title">Menu group</div>
                        <Form.Group className="mb-4">
                            <Form.Label className="pu-modal-field-label">
                                Menu group <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                                options={menuGroupOptions}
                                value={form.applicableMenuGroup}
                                onChange={(opt) => setForm(prev => ({ ...prev, applicableMenuGroup: opt }))}
                                placeholder="Select menu group…"
                                isClearable
                                isSearchable
                                styles={sl}
                            />
                            <p className="pu-modal-field-hint">
                                The menu group whose item prices will be updated.
                            </p>
                        </Form.Group>

                        {/* ── Price change ──────────────────────────────────── */}
                        <div className="pu-modal-section-title">Price change</div>

                        {/* Change type toggle — full width */}
                        <Form.Group className="mb-3">
                            <Form.Label className="pu-modal-field-label">
                                Price Change <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="pu-modal-toggle-group">
                                <button
                                    type="button"
                                    className={`pu-modal-toggle-btn ${form.priceChangeType === 'INCREASE' ? 'active-increase' : ''}`}
                                    onClick={() => setForm(prev => ({ ...prev, priceChangeType: 'INCREASE' }))}
                                >
                                    <TrendingUp size={14} /> Increase
                                </button>
                                <button
                                    type="button"
                                    className={`pu-modal-toggle-btn ${form.priceChangeType === 'DECREASE' ? 'active-decrease' : ''}`}
                                    onClick={() => setForm(prev => ({ ...prev, priceChangeType: 'DECREASE' }))}
                                >
                                    <TrendingDown size={14} /> Decrease
                                </button>
                            </div>
                        </Form.Group>

                        {/* Price Change Based On + value side by side */}
                        <div className="pu-modal-grid mb-4">
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">
                                    Price Change Based On <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="valueType"
                                    value={form.valueType}
                                    onChange={handleFormChange}
                                >
                                    {VALUE_TYPES.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">
                                    Price Change Value <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text style={{ fontSize: '13px' }}>
                                        {form.valueType === 'PERCENTAGE' ? '%' : '₹'}
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        name="changeValue"
                                        value={form.changeValue}
                                        onChange={handleFormChange}
                                        placeholder="0"
                                        min="0"
                                        // max={form.valueType === 'PERCENTAGE' ? 100 : undefined}
                                        style={{ borderLeft: 'none' }}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </div>

                        {/* ── Applicable to ─────────────────────────────────── */}
                        <div className="pu-modal-section-title">Applicable to</div>
                        <p className="pu-modal-mutual-note">
                            Select either categories <b>or</b> products — not both. Note - If you want to apply price change for all categories & products leave the field blank
                        </p>

                        <div className="pu-modal-grid mb-4">
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">Categories</Form.Label>
                                <Select
                                    isMulti
                                    options={categoryOptions}
                                    value={form.applicableCategory}
                                    onChange={handleCategoryChange}
                                    placeholder="Select categories…"
                                    isClearable
                                    isSearchable
                                    styles={sl}
                                    isDisabled={form.applicableProduct.length > 0}
                                />
                                {form.applicableProduct.length > 0 && (
                                    <p className="pu-modal-field-hint">Disabled — products selected.</p>
                                )}
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">Products</Form.Label>
                                <Select
                                    isMulti
                                    options={productOptions}
                                    value={form?.applicableProduct}
                                    onChange={handleProductChange}
                                    placeholder="Select products…"
                                    isClearable
                                    isSearchable
                                    styles={sl}
                                // isDisabled={form.applicableCategory.length > 0}
                                />
                                {/* {form.applicableCategory.length > 0 && (
                                    <p className="pu-modal-field-hint">Disabled — categories selected.</p>
                                )} */}
                            </Form.Group>
                        </div>

                        {/* ── Schedule ──────────────────────────────────────── */}
                        <div className="pu-modal-section-title">Schedule</div>
                        <div className="pu-modal-grid">
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">
                                    Event date <span className="text-danger">*</span>
                                </Form.Label>
                                <DatePicker
                                    selected={form.eventDate}
                                    onChange={(date) => setForm(prev => ({ ...prev, eventDate: date }))}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select date"
                                    minDate={new Date()}
                                    className="form-control"
                                    wrapperClassName="w-100"
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label className="pu-modal-field-label">
                                    <Clock size={13} /> Event time{' '}<span className="text-danger">*</span>
                                    {/* <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span> */}
                                </Form.Label>
                                <Form.Control
                                    type="time"
                                    name="eventTime"
                                    value={form.eventTime}
                                    onChange={handleFormChange}
                                />
                            </Form.Group>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            style={{ fontSize: '13px' }}
                            onClick={() => setShowModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="pu-btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? <><Loader2 size={14} className="me-2" style={{ animation: 'pu-spin 0.8s linear infinite', display: 'inline-block' }} />Saving…</>
                                : 'Create event'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ── Delete confirmation modal ───────────────────────────── */}
                <Modal
                    show={showDeleteModal}
                    onHide={closeDeleteModal}
                    centered
                    className="pu-modal"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete pricing event</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex gap-3 align-items-start">
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: isDark ? '#3a1414' : '#fef2f2',
                                color: isDark ? '#f3a3a3' : '#b91c1c',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AlertCircle size={18} />
                            </div>
                            <div>
                                <p className="mb-1" style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ae-text)' }}>
                                    Are you sure you want to delete this pricing event?
                                </p>
                                <p className="mb-0" style={{ fontSize: '12.5px', color: 'var(--ae-muted)' }}>
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            style={{ fontSize: '13px' }}
                            onClick={closeDeleteModal}
                            disabled={deleting === deleteTargetId}
                        >
                            Cancel
                        </Button>
                        <Button
                            style={{ fontSize: '13px', fontWeight: 600 }}
                            variant="danger"
                            onClick={confirmDelete}
                            disabled={deleting === deleteTargetId}
                        >
                            {deleting === deleteTargetId
                                ? <><Loader2 size={14} className="me-2" style={{ animation: 'pu-spin 0.8s linear infinite', display: 'inline-block' }} />Deleting…</>
                                : 'Delete event'}
                        </Button>
                    </Modal.Footer>
                </Modal>

            </div>
        </React.Fragment>
    );
};

export default PricingUpdateEvents;